// Client de los KPIs de tiendas y vendedores. Las listas y las fichas viven en lambdas distintos:
//
//   GET /backoffice/stores/kpis            → `retail-wizard-business`
//   GET /backoffice/sellers/kpis           → `retail-wizard-business`
//   GET /backoffice/stores/{id}/kpis       → `retail-org-business`
//   GET /backoffice/sellers/{id}/kpis      → `retail-org-business`
//
// Las listas del wizard tienen el eje en la venta: solo aparece quien vendió en la ventana. Tiendas
// y Vendedores las cruzan con el padrón de `retail-org-business` (ver `admin/lib/roster.ts`).
import { apiFetch } from '../../../client';
import type {
    GoalListResponse,
    GoalsApi,
    GoalTargetType,
    KpiWindow,
    SellerKpiDetail,
    SellerKpiRow,
    SellerKpisApi,
    StoreActivityApi,
    StoreKpiDetail,
    StoreKpiRow,
    StoreKpisApi,
} from './contract';

/** Los agregados de lista. */
const WIZARD_URL = import.meta.env.VITE_API_RETAIL_WIZARD_BASE_URL;
/** Los drawers de detalle y las metas. */
const ORG_URL = import.meta.env.VITE_API_RETAIL_ORG_BASE_URL;

/** `StoreKpiRow` tal como lo publica `@fiado/type-kit` para el wizard. */
interface SiloStoreKpiRow {
    storeId: string;
    storeName: string | null;
    zoneName: string | null;
    salesCount: number | null;
    placedCents: number | null;
    averageTicketCents: number | null;
    lastSaleAt: string | null;
    /** Vendido del mismo día de la semana pasada. Solo llega cuando la petición lleva `to`. */
    baselineSameWeekdayCents: number | null;
    baselineSameWeekdayUnits: number | null;
    /** PORCENTAJE (`-91.7`), no fracción: el adaptador lo divide entre 100. */
    weekOverWeekChangePct: number | null;
}

/** La forma de las dos fichas de detalle, tal como responden hoy. Verificada contra dev. */
interface SiloGoal {
    monthCents: number | null;
    weekCents: number | null;
    dayCents: number | null;
}

interface SiloStoreKpiDetail {
    storeId: string;
    name: string | null;
    zoneId: string | null;
    sellersActive: number | null;
    goal: SiloGoal | null;
    sold: number | null;
    goalPct: number | null;
}

interface SiloSellerKpiDetail {
    sellerId: string;
    displayName: string | null;
    store: { storeId: string; name: string | null; zoneId: string | null } | null;
    goal: SiloGoal | null;
    sold: number | null;
    goalPct: number | null;
}

/** `SellerKpiRow` del wizard — ojo con `sellerRetailUserId`, que el front llama `sellerId`. */
interface SiloSellerKpiRow {
    sellerRetailUserId: string;
    sellerName: string | null;
    storeId: string | null;
    storeName: string | null;
    salesCount: number | null;
    placedCents: number | null;
    averageTicketCents: number | null;
    lastSaleAt: string | null;
}

interface SiloListResponse<T> {
    items: T[];
    count: number;
    from: string;
    to: string | null;
    truncated: boolean;
}

/**
 * Traduce la fila del silo a la de la pantalla. Los `null` son deliberados: son los campos que
 * ningún endpoint desplegado entrega. Ver el JSDoc de `StoreKpiRow` en `contract.ts`.
 */
function adaptStoreRow(row: SiloStoreKpiRow): StoreKpiRow {
    return {
        storeId: row.storeId,
        retailerId: null,
        code: null,
        name: row.storeName ?? row.storeId,
        zone: row.zoneName,
        kind: null,
        activeSellerCount: null,
        unitsToday: row.salesCount,
        soldTodayCents: row.placedCents,
        weekOverWeekChange: toFraction(row.weekOverWeekChangePct),
        unitsLastWeek: row.baselineSameWeekdayUnits ?? null,
        soldLastWeekCents: row.baselineSameWeekdayCents ?? null,
        averageTicketCents: row.averageTicketCents,
        dailyGoalCents: null,
    };
}

/** El silo manda porcentaje (`51.6`) y la UI formatea fracción: sin dividir, un +51.6% sale +5160%. */
function toFraction(pct: number | null | undefined): number | null {
    return pct === null || pct === undefined ? null : pct / 100;
}

/** Traduce la ficha de la tienda. Todo lo que el endpoint no manda queda `null` a propósito. */
function adaptStoreDetail(detail: SiloStoreKpiDetail): StoreKpiDetail {
    return {
        storeId: detail.storeId,
        name: detail.name ?? detail.storeId,
        zoneId: detail.zoneId,
        zone: null,
        code: null,
        retailerId: null,
        kind: null,
        activeSellerCount: detail.sellersActive ?? null,
        dailyGoalCents: detail.goal?.dayCents ?? null,
        weeklyGoalCents: detail.goal?.weekCents ?? null,
        monthlyGoalCents: detail.goal?.monthCents ?? null,
        soldTodayCents: detail.sold,
        unitsToday: null,
        salesTodayCount: null,
        averageTicketCents: null,
        weekOverWeekChange: null,
        unitsLastWeek: null,
        financierMix: null,
        sellers: null,
        soldThisWeekCents: null,
        unitsThisWeek: null,
        cancellations7d: null,
    };
}

function adaptSellerDetail(detail: SiloSellerKpiDetail): SellerKpiDetail {
    return {
        sellerId: detail.sellerId,
        name: detail.displayName ?? detail.sellerId,
        storeId: detail.store?.storeId ?? null,
        storeName: detail.store?.name ?? null,
        storeZoneId: detail.store?.zoneId ?? null,
        retailerId: null,
        zone: null,
        storeCode: null,
        isStoreManager: null,
        dailyGoalCents: detail.goal?.dayCents ?? null,
        weeklyGoalCents: detail.goal?.weekCents ?? null,
        monthlyGoalCents: detail.goal?.monthCents ?? null,
        soldTodayCents: detail.sold,
        unitsToday: null,
        todaySales: null,
        sold7dCents: null,
        units7d: null,
        averageTicket7dCents: null,
    };
}

/**
 * `?from=&to=` de la ventana pedida. Sin ventana no se manda nada y el silo cae a 90 días — que es
 * lo que quieren los dos consumidores que usan estas listas como PADRÓN (Análisis y Excepciones).
 */
function windowQuery(kpiWindow: KpiWindow | undefined): string {
    if (!kpiWindow) return '';
    const params = new URLSearchParams({ from: kpiWindow.from, to: kpiWindow.to });
    return `?${params.toString()}`;
}

function adaptSellerRow(row: SiloSellerKpiRow): SellerKpiRow {
    return {
        sellerId: row.sellerRetailUserId,
        name: row.sellerName ?? row.sellerRetailUserId,
        storeId: row.storeId,
        retailerId: null,
        storeName: row.storeName,
        zone: null,
        isStoreManager: null,
        unitsToday: row.salesCount,
        soldTodayCents: row.placedCents,
        averageTicketCents: row.averageTicketCents,
        dailyGoalCents: null,
    };
}

export const storeKpisClient: StoreKpisApi = {
    listStores: async (kpiWindow?: KpiWindow) => {
        const silo = await apiFetch<SiloListResponse<SiloStoreKpiRow>>(
            `/backoffice/stores/kpis${windowQuery(kpiWindow)}`,
            { baseUrl: WIZARD_URL },
        );
        return { items: silo.items.map(adaptStoreRow), truncated: silo.truncated };
    },
    getStore: async (storeId: string) =>
        adaptStoreDetail(
            await apiFetch<SiloStoreKpiDetail>(
                `/backoffice/stores/${encodeURIComponent(storeId)}/kpis`,
                { baseUrl: ORG_URL },
            ),
        ),
};

export const sellerKpisClient: SellerKpisApi = {
    listSellers: async (kpiWindow?: KpiWindow) => {
        const silo = await apiFetch<SiloListResponse<SiloSellerKpiRow>>(
            `/backoffice/sellers/kpis${windowQuery(kpiWindow)}`,
            { baseUrl: WIZARD_URL },
        );
        return { items: silo.items.map(adaptSellerRow), truncated: silo.truncated };
    },
    getSeller: async (sellerId: string) =>
        adaptSellerDetail(
            await apiFetch<SiloSellerKpiDetail>(
                `/backoffice/sellers/${encodeURIComponent(sellerId)}/kpis`,
                { baseUrl: ORG_URL },
            ),
        ),
};

/**
 * Metas del periodo. Viven en `retail-org-business`, no en el wizard.
 *
 * ⚠️ Requiere la tabla `SureKeepRetailGoal_GT`. Mientras no exista, el endpoint responde error y la
 * pantalla se queda sin barra de avance — que es exactamente lo que tiene que pasar: una barra en
 * cero diría «no vendieron», y la verdad es «no sabemos cuál era la meta».
 */
export const goalsClient: GoalsApi = {
    listGoals: (periodId: string, targetType: GoalTargetType) =>
        apiFetch<GoalListResponse>(
            `/backoffice/goals?periodId=${encodeURIComponent(periodId)}&targetType=${targetType}`,
            { baseUrl: ORG_URL },
        ),
};

/** Actividad de las tiendas en la ventana: la fila del silo sin descartar `lastSaleAt` ni el delta. */
export const storeActivityClient: StoreActivityApi = {
    listStoreActivity: async (kpiWindow: KpiWindow) => {
        const silo = await apiFetch<SiloListResponse<SiloStoreKpiRow>>(
            `/backoffice/stores/kpis${windowQuery(kpiWindow)}`,
            { baseUrl: WIZARD_URL },
        );
        return {
            items: silo.items.map((row) => ({
                storeId: row.storeId,
                storeName: row.storeName,
                zoneName: row.zoneName,
                salesCount: row.salesCount,
                placedCents: row.placedCents,
                averageTicketCents: row.averageTicketCents,
                lastSaleAt: row.lastSaleAt,
                baselineCents: row.baselineSameWeekdayCents ?? null,
                weekOverWeekChangePct: row.weekOverWeekChangePct ?? null,
            })),
            truncated: silo.truncated,
        };
    },
};
